import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, CalendarDays } from "lucide-react";

export const CATEGORY_LABELS = { dine: "Dine", stay: "Stay", dock: "Dock", gather: "Gather" };
export const CATEGORY_COPY = {
  dine: "Restaurants, cocktails, seafood, steaks and seasonal waterfront dining.",
  stay: "Boutique inns, cottages and waterfront accommodations.",
  dock: "Marinas and waterfront destinations throughout Coastal Virginia.",
  gather: "Weddings, private dining, corporate gatherings and celebrations.",
};

export function fmtEventDate(d) {
  const dt = new Date(d + "T12:00:00");
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export function ctaForProperty(p) {
  if (p.status === "coming_soon" || p.status === "development") {
    const short = p.name.replace(/^The\s+/, "").split(" ").slice(0, 3).join(" ");
    return `Discover ${short}`;
  }
  if (p.categories?.includes("dine")) return "Reserve a Table";
  if (p.categories?.includes("stay")) return "Book Your Stay";
  if (p.categories?.includes("dock")) return "Explore the Marina";
  return "Visit Property";
}

export function SectionHeading({ eyebrow, title, intro, align = "left", light = false, className = "" }) {
  const center = align === "center";
  return (
    <div className={`${center ? "mx-auto max-w-2xl text-center" : "max-w-2xl"} ${className}`}>
      {eyebrow && (
        <p className={`mb-3 text-xs font-semibold uppercase tracking-[0.18em] ${light ? "text-[#B7A58E]" : "text-[#526E7E]"}`}>
          {eyebrow}
        </p>
      )}
      <h2 className={`font-serif text-[34px] leading-[1.1] sm:text-[44px] ${light ? "text-white" : "text-[#18344A]"}`}>
        {title}
      </h2>
      {intro && <p className={`mt-4 text-[15px] leading-relaxed ${light ? "text-white/75" : "text-[rgba(48,56,60,0.72)]"}`}>{intro}</p>}
    </div>
  );
}

export function PageHeader({ eyebrow, title, intro, icon: Icon, children }) {
  return (
    <section className="bg-[#18344A] py-20 text-white">
      <div className="mx-auto max-w-[1280px] px-5 lg:px-8">
        {Icon && <Icon className="mb-4 h-10 w-10 text-[#B7A58E]" />}
        <SectionHeading eyebrow={eyebrow} title={title} intro={intro} light />
        {children}
      </div>
    </section>
  );
}

function StatusPill({ status }) {
  if (status === "coming_soon") return <span className="rounded-full bg-[#B7A58E] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#18344A]">Coming Soon</span>;
  if (status === "development") return <span className="rounded-full bg-white/85 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#18344A]">In Development</span>;
  return null;
}

export function PropertyCard({ p, large = false }) {
  return (
    <Link
      to={`/portfolio/${p.slug}`}
      data-testid={`property-card-${p.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-[var(--pb-border)] bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(24,52,74,0.14)]"
    >
      <div className={`relative overflow-hidden ${large ? "aspect-[16/10]" : "aspect-[4/3]"}`}>
        <img
          src={p.image}
          alt={p.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
        />
        <div className="absolute left-4 top-4"><StatusPill status={p.status} /></div>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <div className="mb-2 flex flex-wrap gap-1.5">
          {(p.categories || []).map((c) => (
            <span key={c} className="rounded-full bg-[rgba(82,110,126,0.12)] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#526E7E]">
              {CATEGORY_LABELS[c]}
            </span>
          ))}
        </div>
        <h3 className={`font-serif ${large ? "text-3xl" : "text-2xl"} leading-tight text-[#18344A]`}>{p.name}</h3>
        <p className="mt-1 flex items-center gap-1.5 text-xs uppercase tracking-wide text-[#78909C]">
          <MapPin className="h-3.5 w-3.5" /> {p.city}, {p.state}
        </p>
        <p className="mt-3 line-clamp-2 flex-1 text-sm leading-relaxed text-[rgba(48,56,60,0.72)]">{p.tagline || p.description}</p>
        <span className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#18344A]">
          {ctaForProperty(p)} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

export function EventCard({ e }) {
  return (
    <Link
      to={`/happenings/${e.slug}`}
      data-testid={`event-card-${e.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-[var(--pb-border)] bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(24,52,74,0.12)]"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={e.image} alt={e.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
        <div className="absolute left-4 top-4 rounded-lg bg-white/95 px-3 py-1.5 text-center shadow">
          <div className="font-serif text-lg leading-none text-[#18344A]">{fmtEventDate(e.date)}</div>
        </div>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#526E7E]">{e.property_name}</p>
        <h3 className="mt-1 font-serif text-xl leading-tight text-[#18344A]">{e.title}</h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm text-[rgba(48,56,60,0.7)]">{e.description}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#18344A]">
          View Event <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

export function PressCard({ p }) {
  const isAward = p.type === "award";
  const body = (
    <div className="flex h-full flex-col rounded-2xl border border-[var(--pb-border)] bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_36px_rgba(24,52,74,0.1)]">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#526E7E]">{p.publication}</span>
        {isAward && <span className="rounded-full bg-[rgba(183,165,142,0.22)] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#7a6a4f]">Accolade</span>}
      </div>
      <h3 className="mt-3 flex-1 font-serif text-2xl leading-tight text-[#18344A]">{p.headline}</h3>
      {p.excerpt && <p className="mt-3 text-sm text-[rgba(48,56,60,0.7)]">{p.excerpt}</p>}
      <div className="mt-5 flex items-center justify-between text-xs text-[#78909C]">
        <span>{new Date(p.date + "T12:00:00").toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
        {p.url && <span className="font-semibold text-[#18344A]">Read More →</span>}
      </div>
    </div>
  );
  return p.url ? (
    <a href={p.url} target="_blank" rel="noreferrer" data-testid="press-card">{body}</a>
  ) : (
    <div data-testid="press-card">{body}</div>
  );
}

export function Loader() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#526E7E] border-t-transparent" />
    </div>
  );
}
